// Static, verified editorial copy from the DBRF master build prompt (UK English).
// No fabricated clients, metrics, people, results or historical assertions.

export const NAV = [
  { label: "What We Solve", to: "/what-we-solve" },
  { label: "Capabilities", to: "/capabilities" },
  { label: "Network", to: "/network" },
  { label: "Projects", to: "/projects" },
  { label: "Heritage", to: "/heritage" },
  { label: "About", to: "/about" },
];

export const HERO_ALT = {
  home: "Scientists and engineers converging across a multi-level research atrium.",
  "what-we-solve": "Experts examining sensor data at an industrial research site.",
  capabilities: "A multi-discipline working studio seen from near overhead.",
  network: "A distributed mosaic of laboratories and workplaces across locations.",
  projects: "Hands, a prototype board and test instruments at a precision bench.",
  heritage: "Concept art evoking a historical physics archive.",
  about: "A group in an institutional scientific library and foundation hall.",
  "bring-a-challenge": "A technical review along a data-centre operations aisle.",
  "join-the-network": "Researchers and engineers in a university innovation forum.",
};

export const PROOF_STRIP = ["Problem-led", "Interdisciplinary", "Evidence-aware", "Built toward implementation"];

export const DISCIPLINE_NODES = [
  { key: "ai", label: "AI and machine learning", blurb: "Learning systems where behaviour, data and measurable performance matter.", filter: "AI / ML systems and LLM architecture" },
  { key: "neuro", label: "Neuroscience and cognition", blurb: "Cognition and human factors, examined rather than mythologised.", filter: "Neuroscience, cognition and human factors" },
  { key: "math", label: "Mathematics and information theory", blurb: "Provable efficiency and representation under real constraints.", filter: "Information theory and applied mathematics" },
  { key: "signal", label: "Signal, media and perception", blurb: "Analysis and synthesis of signals across modalities.", filter: "Signal, audio and media systems" },
  { key: "software", label: "Software and systems engineering", blurb: "Reliable, reproducible engineering for research and technical workflows.", filter: "Cloud, edge and infrastructure engineering" },
  { key: "trust", label: "Distributed trust and data integrity", blurb: "Verifiable provenance assessed against real threat models.", filter: "Security, provenance and distributed trust" },
  { key: "ip", label: "Product, IP and commercialisation", blurb: "Translating research toward defensible, transferable assets.", filter: "Product, IP, validation and commercial translation" },
];

export const HOW_STAGES = [
  { n: "01", title: "Frame", desc: "Define the problem, constraints and outcome.", output: "Output: a shared problem definition." },
  { n: "02", title: "Map", desc: "Identify the science, engineering and evidence required.", output: "Output: a capability and evidence map." },
  { n: "03", title: "Assemble", desc: "Form a focused interdisciplinary team.", output: "Output: a scoped, governed team." },
  { n: "04", title: "Build", desc: "Model, prototype, test and document.", output: "Output: prototype and documentation." },
  { n: "05", title: "Transfer", desc: "Deliver technology, IP, licensing or the next stage.", output: "Output: a transferable asset or next step." },
];

export const CAPABILITY_DOMAINS = [
  { title: "Information architecture (MSML / IDDO)", scope: "Formalising structure, representation and density so information can be reasoned about precisely.", tags: ["Information theory", "Representation", "Density"], outputs: "Methods, architecture, documentation", filter: "Information architecture (MSML / IDDO)" },
  { title: "AI information efficiency", scope: "Efficiency of LLM, context and AI pipelines under real constraints.", tags: ["LLM systems", "Inference", "Retrieval"], outputs: "Architecture, prototype, evidence report", filter: "AI information efficiency" },
  { title: "Acoustic intelligence and media", scope: "Audio encoding, recognition and structural representation across media.", tags: ["DSP", "Audio fingerprinting", "Acoustics"], outputs: "Prototype, software, evidence report", filter: "Acoustic intelligence and media" },
  { title: "Media trust and provenance", scope: "Verifiable media identity, provenance and tamper-evident records.", tags: ["Provenance", "Cryptography", "Ledgers"], outputs: "Architecture, evidence report", filter: "Media trust and provenance" },
  { title: "Cloud and edge infrastructure", scope: "Synchronisation and media processing across cloud and edge environments.", tags: ["Distributed systems", "Edge", "Synchronisation"], outputs: "Software, architecture", filter: "Cloud and edge infrastructure" },
  { title: "Human-impact intelligence", scope: "Responsible AI, psychometrics and real-time human-state modelling.", tags: ["Responsible AI", "Psychometrics", "Product"], outputs: "Feasibility study, prototype, evidence report", filter: "Human-impact intelligence" },
  { title: "Infrastructure validation and economics", scope: "Pilot architecture, economic modelling and technical diligence.", tags: ["Benchmarking", "Economics", "Diligence"], outputs: "White paper, pilot framework, IP-supporting materials", filter: "Infrastructure validation and economics" },
];

export const SELECTED_WORK = [
  { title: "Multimodal signal intelligence", slug: "multimodal-signal-intelligence" },
  { title: "Brain-inspired information architecture", slug: "brain-inspired-information-architecture" },
  { title: "Trusted provenance for scientific and media systems", slug: "trusted-provenance-scientific-media" },
];

export const PROBLEM_CATEGORIES = [
  { title: "Information burden in AI and data pipelines", facing: "Memory, context, retrieval, network, storage and compute constraints that scale faster than value.", spans: "Information theory, ML systems, infrastructure engineering.", package: "Workload decomposition, insertion-point analysis and quality-equivalence testing.", outputs: "Methods, prototype, validation criteria." },
  { title: "Media continuity and machine perception", facing: "Representation, recognition, timing, identity and movement across systems.", spans: "Signal/audio systems, distributed systems, provenance.", package: "Representation, recognition, synchronisation and provenance design.", outputs: "Algorithms, prototype, architecture." },
  { title: "Human impact of AI interaction", facing: "Clarity, cognitive load, emotional pressure, agency and responsible assistance.", spans: "Neuroscience, psychometrics, responsible AI.", package: "Dimension design, scoring architecture and privacy-preserving pilots.", outputs: "Methods, prototype, pilot framework." },
  { title: "Translation of deep IP into measurable pilots", facing: "Research that needs a route to architecture, prototype, evidence and a commercial pathway.", spans: "Systems architecture, validation, commercial translation.", package: "Architecture, prototype, evidence plan and validation.", outputs: "White paper, pilot framework, licensing pathway." },
];

export const PORTFOLIO_GATEWAYS = [
  { title: "Information-efficiency architecture", blurb: "Structural representation, useful information density and workload-specific optimisation.", filter: "Information architecture (MSML / IDDO)" },
  { title: "Acoustic Intelligence", blurb: "Efficient audio representation, recognition, synchronisation, provenance and connected-media infrastructure.", filter: "Acoustic intelligence" },
  { title: "Human-Impact Intelligence", blurb: "Neuroscience-informed measurement and improvement of AI's effects on clarity, agency and emotional load.", filter: "Human-impact intelligence" },
  { title: "AI infrastructure validation", blurb: "White papers, pilot architecture, KPI design and economic translation for AI/data-centre workloads.", filter: "Infrastructure validation" },
];

export const HOME_PORTFOLIO_INTRO =
  "DBRF's OEM work spans formal information architecture, signal and media systems, human-centred AI, cloud platforms and infrastructure-validation methods. Confidentiality limits what can be disclosed, so each published record separates the technical challenge, DBRF contribution, evidence state and rights position.";

export const CHALLENGE_DOMAINS = [
  "AI information efficiency", "Data-centre and compute economics", "RAG, context and agent memory",
  "Acoustic intelligence and media systems", "Recognition and multimodal perception",
  "Synchronisation and connected experiences", "Provenance, identity and trust",
  "Human-impact intelligence and responsible AI", "Health-adjacent information-value research",
  "Scientific software and infrastructure", "Technology/IP translation",
];

export const PROJECT_FAMILIES = [
  "Information-efficiency architecture", "Acoustic intelligence", "Connected media",
  "Media trust and provenance", "Cloud and edge media", "Human-impact intelligence",
  "AI infrastructure validation",
];

export const PREFERRED_CONTRIBUTION = [
  "Research", "Architecture", "Engineering", "Validation", "Technical writing", "Product", "IP support",
];

export const ABOUT_OEM = {
  heading: "OEM development without the usual disciplinary walls.",
  copy: "DBRF can operate behind a partner's product and intellectual-property programme, assembling the scientific, engineering and documentation capability required for a defined work package. Public visibility is not the measure of contribution. Clear scope, evidence, confidentiality and transfer are.",
};

export const EVIDENCE_DISTINCTIONS = [
  ["Patent status", "Performance evidence"],
  ["Public product existence", "Validated results"],
  ["Company-reported demonstration", "Independent validation"],
  ["Technical target", "Achieved result"],
  ["Modelled economics", "Realised customer value"],
  ["OEM contribution", "IP ownership"],
];

export const EVIDENCE_LABELS = [
  { label: "Demonstrated", d: "A working implementation or documented test exists." },
  { label: "In development", d: "Active technical work; not production-ready." },
  { label: "Pilot", d: "A scoped implementation is being evaluated." },
  { label: "Research framework", d: "A technical or economic method to guide validation; not a performance result." },
  { label: "Historical implementation", d: "Prior working development; current availability must be confirmed." },
  { label: "Public product", d: "An externally visible product exists; this does not validate every performance statement." },
  { label: "Patent record", d: "A verified filed or granted record; do not infer product performance from patent status." },
];

export const GOOD_FIT = [
  "High-value unresolved question",
  "Multiple scientific or technical dependencies",
  "Need for research, architecture or prototype — not merely implementation capacity",
  "Sponsor can provide context, decision access and relevant data",
  "Clear willingness to define evidence and success criteria",
];

export const NOT_FIT = [
  "Commodity software development",
  "Staff augmentation without a research or architecture problem",
  "Unbounded ‘build us an AI’ requests",
  "Work requiring claims that cannot be independently tested",
  "Projects without lawful access to the required data or rights",
];

export const ENGAGEMENT_ROUTES = [
  "Discovery and technical framing",
  "Feasibility study",
  "Research sprint",
  "Architecture and prototype",
  "Extended R&D program",
  "IP development or licensing pathway",
];

export const ABOUT_IS = [
  "A problem-led scientific and engineering platform",
  "A structured network of interdisciplinary capability",
  "An operating framework for research, prototyping and technology transfer",
  "A steward of scientific heritage and forward-looking technical inquiry",
];

export const ABOUT_IS_NOT = [
  "Not a general recruitment marketplace",
  "Not a commodity software agency",
  "Not a laboratory claiming every capability in-house",
  "Not a publisher of unverified scientific or commercial claims",
];

export const PRINCIPLES = [
  { t: "Scientific integrity", d: "Distinguish evidence, hypothesis and ambition." },
  { t: "Accountability", d: "Define responsibility, decisions and outputs." },
  { t: "Confidentiality", d: "Protect sensitive information and independence." },
  { t: "Interdisciplinary respect", d: "Build around contribution, not status." },
  { t: "Inclusive participation", d: "Evaluate professional capability without irrelevant barriers." },
  { t: "Practical translation", d: "Connect research to testable technical work." },
  { t: "Long-term stewardship", d: "Respect intellectual property, provenance and institutional memory." },
];

export const HERITAGE_THEMES = [
  { then: "Quantum electronics", now: "Information-bearing physical systems" },
  { then: "Molecular computation", now: "Computation beyond conventional abstractions" },
  { then: "Signal physics", now: "Multimodal perception and representation" },
  { then: "Interdisciplinary research", now: "Problem-specific technical teams" },
];

export const COMMUNITY_CODE = [
  "Be precise about what is known and unknown",
  "Protect confidential and proprietary information",
  "Disclose conflicts",
  "Respect contribution and attribution",
  "Work constructively across disciplines",
  "Do not overstate evidence or readiness",
];

export const WHO_SHOULD_APPLY = [
  "Researchers with demonstrable depth",
  "Engineers able to translate science into systems",
  "Specialists comfortable working across disciplinary language",
  "Technical leaders who can define evidence and limitations",
  "Contributors who respect confidentiality, attribution and intellectual property",
];

export const PLACEHOLDER = {
  email: "hello@[dbrf-domain].org",
  linkedin: "linkedin.com/company/[dbrf-placeholder]",
  office: "Registered office — to be confirmed (placeholder for verified content)",
  entity: "Digital Brain Research Foundation LLP",
};

export const GOVERNANCE = {
  eyebrow: "Governance & legal identity",
  heading: "Built for complex work. Governed for trust.",
  intro:
    "DBRF brings scientific thinking, engineering discipline and responsible governance into one operating framework. Our structure is designed for multidisciplinary programmes where advanced research, technology, intellectual property and commercial execution must work together without compromising scientific integrity or confidentiality.",
  pillars: [
    {
      key: "leadership",
      title: "Leadership",
      body: [
        "DBRF is built around multidisciplinary leadership rather than a single field of expertise. Scientific research, engineering, technology strategy, intellectual property, programme development and commercial execution are considered together from the beginning of each engagement.",
        "This structure allows complex ideas to move from first principles and scientific investigation toward practical systems, prototypes and deployable solutions while preserving the original technical intent.",
      ],
      tags: ["Scientific direction", "Technology strategy", "Programme governance", "Commercial translation"],
    },
    {
      key: "advisory",
      title: "Scientific Advisory Group",
      body: [
        "DBRF programmes can draw on specialist scientific and technical expertise appropriate to the problem being addressed. The objective is not simply to review an idea, but to challenge its assumptions, examine the underlying physics and mathematics, and identify the disciplines required to advance it.",
        "This approach is particularly important where a programme crosses traditional boundaries between fundamental science, information systems, artificial intelligence, computation, signal processing and emerging technologies.",
      ],
      tags: ["Scientific review", "Mathematical foundations", "Systems thinking", "Cross-disciplinary research"],
    },
    {
      key: "partners",
      title: "Operating Partners",
      body: [
        "Complex technology rarely reaches the real world through one organisation alone. DBRF is structured to work with specialist laboratories, engineers, technology companies, intellectual-property professionals and commercial organisations as each programme requires.",
        "Teams can therefore be assembled around the challenge rather than forcing the challenge into a predetermined organisational structure. Participation, information access and responsibilities can be defined according to the technical, commercial and confidentiality requirements of each programme.",
      ],
      tags: ["Specialist teams", "Flexible programme structures", "Controlled collaboration", "Execution capability"],
    },
    {
      key: "principles",
      title: "Governance Principles",
      body: [
        "Advanced research requires freedom to explore, but serious programmes also require discipline. DBRF applies clear responsibility, evidence-based decision making, controlled information access and defined intellectual-property boundaries throughout the development process.",
        "Scientific uncertainty is identified rather than hidden. Technical claims are separated from hypotheses. Confidential information is protected. Material decisions are documented so that collaborators, rights holders and programme stakeholders can understand how a project is being advanced.",
      ],
      tags: ["Evidence", "Accountability", "Confidentiality", "Intellectual-property discipline"],
    },
  ],
  legal: {
    eyebrow: "Legal identity",
    name: "Digital Brain Research Foundation LLP",
    intro:
      "Digital Brain Research Foundation LLP is established in England and Wales and operates through formal legal and governance structures supporting its research, development and collaborative activities. Intellectual-property, confidentiality and programme arrangements are structured according to the requirements of each engagement and the rights of the participating organisations.",
    fields: [
      { label: "Legal name", value: "Digital Brain Research Foundation LLP" },
      { label: "Organisation", value: "DBRF" },
      { label: "Registered jurisdiction", value: "England and Wales" },
      { label: "Registered office", value: "71–75 Shelton Street, Covent Garden, London, WC2H 9JQ\nUnited Kingdom" },
      { label: "Registration number", value: "OC302112" },
    ],
  },
  trust: {
    heading: "Serious science requires serious stewardship.",
    copy:
      "From exploratory research to commercially sensitive technology programmes, DBRF is designed to protect the integrity of the science, the interests of collaborators and the intellectual property created along the way.",
    ctaLabel: "Discuss a programme",
    ctaTo: "/bring-a-challenge",
  },
};
